import { useCallback, useState } from 'react';
import type { ManualEntryItem } from './types';

const STORAGE_KEY = 'equip:bulk-dispose:manual-entries';

const readFromStorage = (): ManualEntryItem[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeToStorage = (items: ManualEntryItem[]): void => {
  try {
    if (items.length === 0) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    }
  } catch {
    // localStorage may be full or unavailable; silently ignore
  }
};

export const useManualEntryStorage = () => {
  const [items, setItems] = useState<ManualEntryItem[]>(readFromStorage);

  const addItem = useCallback((item: ManualEntryItem) => {
    setItems((prev) => {
      const next = [...prev, item];
      writeToStorage(next);
      return next;
    });
  }, []);

  const removeItem = useCallback((id: string) => {
    setItems((prev) => {
      const next = prev.filter((item) => item.id !== id);
      writeToStorage(next);
      return next;
    });
  }, []);

  const clearItems = useCallback(() => {
    setItems([]);
    writeToStorage([]);
  }, []);

  return { items, addItem, removeItem, clearItems } as const;
};
